/**
 * Only what the engraving machine can reproduce is accepted: the unaccented
 * latin letters A-Z (lowercase ones are uppercased) and the digits 0-9.
 * Everything else - umlauts and ß, accented letters, arrows, symbols,
 * punctuation, spaces, emoji, non-latin scripts, invisible characters - is
 * stripped from the monogram inputs so it can never reach the order.
 */
const MONOGRAM_FORBIDDEN_CHARS = /[^A-Za-z0-9]/g;

class PoMonogram extends HTMLElement {
  constructor() {
    super();
    this.textInputs = this.querySelectorAll('.js-text-inputs input');

    this.inputFontFieldset = this.querySelector('.js-font-inputs');
    this.fontInputs = this.querySelectorAll('.js-font-inputs input');
    this.dynamicTextArea = this.querySelector('.js-monogram-dynamic-text');

    this.applyButton = this.querySelector('.js-btn-apply-monogram');

    this.productFormAddToCart = this.closest('product-info')?.querySelector('.js-pdp-po-main-buy-buttons product-form form');

    this.monogramTextModalOpener = this.querySelector('.js-monogram-text-modal-opener');
    this.monogramNotAppliedElements = this.querySelectorAll('.js-monogram--not-applied');
    this.monogramAppliedElements = this.querySelectorAll('.js-monogram--applied');

    this.removeButton = this.querySelector('.js-remove-monogram-button');
  }

 connectedCallback() {
    this.textInputs.forEach((input, index) => {
      
      // Gestione Input (avanzamento automatico)
      input.addEventListener('input', (event) => {
        // Drop the characters that cannot be engraved, leaving a valid value untouched
        const allowedValue = input.value.replace(MONOGRAM_FORBIDDEN_CHARS, '');
        if (allowedValue !== input.value) input.value = allowedValue;

        this.checkTextInputs(this.textInputs);

        // Se è stato inserito un carattere e non siamo all'ultimo input, passa al focus successivo
        if (input.value.length === 1 && index < this.textInputs.length - 1) {
          this.textInputs[index + 1].focus();
        }
      });

      // Gestione Tasti (es. Backspace per tornare indietro)
      input.addEventListener('keydown', (event) => {
        // Se preme Backspace, il campo è vuoto e non siamo al primo input, torna indietro
        if (event.key === 'Backspace' && input.value === '' && index > 0) {
          this.textInputs[index - 1].focus();
        }
      });
    });

    this.inputFontFieldset.addEventListener('change', (event) => {
      this.toggleCheckedInput(this.fontInputs, event)
      this.applyButton.setAttribute('data-monogram-font', event.target.value);      
    });

    this.applyButton.addEventListener('click', (event) => {
      this.handleApplyMonogram(this.applyButton);
    });

    this.removeButton.addEventListener('click', (event) => {
      this.handleRemoveMonogram();
    });

    /**
     * Handle monogramRemoved events from other components.
     * If the event source is external, remove the monogram without re-publishing to avoid an infinite loop.
     */
    subscribe(PUB_SUB_EVENTS.monogramRemoved, (event) => {
      if (event.source != 'internal') {
        this.handleRemoveMonogram(false);
      }
    });
  }

  /**
   * Checks if any of the given inputs contain text.
   * @param {NodeList} inputs - The inputs to check.
   */
  checkTextInputs(inputs) {

    const combinedText = 
      Array.from(inputs)      // Convert the NodeList of input elements into a real array
      .map(input => input.value.trim())     // For each input, get the text value and trim whitespace
      .filter(value => value !== '')        // Remove any empty strings
      .join('')                             // Join the remaining strings into one, with no separator
      .toUpperCase();              
    
    this.applyButton.setAttribute('data-monogram-text', combinedText);

    this.dynamicTextArea.textContent = 
      combinedText  // Take the combined string of all non-empty input values
      .split('')    // Split the string into an array of individual characters
      .map(char => char + '.')     // For each character, append a dot
      .join(' ')                   // Join the array back into a single string with a space
      .toUpperCase();   
       
    if (combinedText != '') {
      this.applyButton.removeAttribute('disabled');
    } else {
      this.applyButton.setAttribute('disabled', '');
    }
  }

  /**
   * @param {NodeList} inputs - The inputs to check.
   * @param {Event} event - The change event triggered by selecting a font input.
   */
  toggleCheckedInput(inputs, event) {
    inputs.forEach(input => {
      if (input.value === event.target.value) {
        input.setAttribute('checked', '');
        this.dynamicTextArea.style.fontFamily=input.dataset.fontFamily;
      } else {
        input.removeAttribute('checked');
      }
    });
  }

  /**
   * @param {HTMLElement} element - Element with necessary data.
   */
  handleApplyMonogram(element) {
    if (element.dataset.monogramVariantId != '' && element.dataset.monogramText != '') {
      let input = this.productFormAddToCart.querySelector('input[name="monogram-input"]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'monogram-input';
        input.className = 'js-nested-cart-item';

        // Copy dataset values
        input.dataset.monogramVariantId = element.dataset.monogramVariantId;
        input.dataset.monogramText = element.dataset.monogramText;
        input.dataset.monogramFont = element.dataset.monogramFont;

        this.productFormAddToCart.appendChild(input);
      } else {
        input.dataset.monogramVariantId = element.dataset.monogramVariantId;
        input.dataset.monogramText = element.dataset.monogramText;
        input.dataset.monogramFont = element.dataset.monogramFont;
      }

      if (this.monogramTextModalOpener) {
        this.monogramTextModalOpener.textContent = element.dataset.monogramText;
      }

      this.updateTextElements(true); // Update UI


      // Publish an event to update elements outside of this custom element
      publish(PUB_SUB_EVENTS.monogramApplied, {
        monogramText: element.dataset.monogramText,
        monogramFont: element.dataset.monogramFont
      });
    }
  }
  
  /** 
   * Removes the monogram from the product form and resets UI elements.
   * This method is triggered either:
   * 1. Internally — when the user clicks the remove button within this component.
   * 2. Externally — when another component publishes a `monogramRemoved` event (e.g., an alert drawer).
   * 
   * @param {boolean} [shouldPublish] - Whether to re-publish the removal event. Set false for external triggers to avoid loops.
   */
  handleRemoveMonogram(shouldPublish = true) {
    let input = this.productFormAddToCart.querySelector('input[name="monogram-input"]');
    
    if (input) {
      input.remove();

      this.resetElements();

      this.removeButton.blur(); // Avoid console accessibility warnings for focused and aria-hidden elements

      this.updateTextElements(false); // Update UI

      // Publish an event to update elements outside of this custom element
      if (shouldPublish) {
        publish(PUB_SUB_EVENTS.monogramRemoved, {source: 'internal'});
      }

      // Hide PoMonogramAlertDrawer 
      let poMonogramAlertDrawer = document.querySelector('po-monogram-alert-drawer');
      if (poMonogramAlertDrawer) {
        poMonogramAlertDrawer.setAttribute('hidden', '');
      }
    }
  }

  resetElements() {
    // Clear the text inputs
    this.textInputs.forEach(input => input.value = '');

    // Clear the dynamic text area
    this.dynamicTextArea.textContent = '';

    // Reset the apply button state
    this.applyButton.setAttribute('disabled', '');
    this.applyButton.setAttribute('data-monogram-text', '');

    // Reset font selection to default (first input checked)
    if (this.fontInputs.length) {
      this.fontInputs.forEach((input, i) => {
        input.checked = i === 0; // first font becomes selected
      });

      this.dynamicTextArea.style.fontFamily = this.fontInputs[0].dataset.fontFamily;
    }

    // Clear modal opener text
    if (this.monogramTextModalOpener) {
      this.monogramTextModalOpener.textContent = '';
    }

  }

  updateTextElements(monogramApplied) {
    this.monogramNotAppliedElements.forEach(element => {
      element.setAttribute('aria-hidden', monogramApplied);
    });
    this.monogramAppliedElements.forEach(element => {
      element.setAttribute('aria-hidden', !monogramApplied);
    });
  }
}

customElements.define('po-monogram', PoMonogram);
class PoMonogramAlertDrawer extends HTMLElement {
  constructor() {
    super();
    this.productType = this.dataset.productType;
    this.alertRemoveButton = this.querySelector('[data-alert-monogram-remove]');
  }

  connectedCallback() {
    // When the monogram is added, it makes clickable the button that covers the RXC CTA
    subscribe(PUB_SUB_EVENTS.monogramApplied, (event) => {
      this.removeAttribute('hidden');
    });
    
    this.alertRemoveButton.addEventListener('click', (event) => {
      // The event is published for certain product types. Currently, this is only for sunglasses.
      if (this.productType == 'sun') {
        publish(PUB_SUB_EVENTS.monogramRemoved, {source: 'external'});
      }
      this.handleHideDrawer();
      this.triggerClickRxcBtn();
    });
  }

  // Hides the drawer only if the monogram hidden input does not exist
  handleHideDrawer() {
    let input = document.querySelector('input[name="monogram-input"]');
    if (!input) {
      this.setAttribute('hidden', '');
    }
  }

  // Trigger the click of the CTA that opens RXC modal
  triggerClickRxcBtn() {
    let rxcButton = document.querySelector('button[name="rxc-activator"]');
    if (rxcButton) {
      rxcButton.click();
    }
  }
}

customElements.define('po-monogram-alert-drawer', PoMonogramAlertDrawer);